/**
 * file-selekt component - file inputs with upload-on-select.
 *
 * The original <input type="file"> stays only as the PICKER (hidden; we
 * .click() it). The VALUE lives in a created hidden input carrying the file
 * jsonb as JSON - an object for single, an array for multiple - exactly what
 * the save endpoint expects and what the DB stores. Every picked file is
 * uploaded IMMEDIATELY (once) to opts.endpoint -> temp storage -> the
 * response jsonb fills the value. Crop never re-uploads: it only attaches
 * crop coordinates to the jsonb (the server applies them at save).
 *
 * Modes: simple | dropzone | image | image-editor | gallery.
 * Full reference: docs/libs/file-selekt.md.
 */
import { parseAccept, typeOf, extOf, iconOf, humanSize, maybeDownscale, uploadFile, displayUrl, drawCropPreview } from './upload.js';
import { openEditor } from './editor.js';

const t = (s) => (window.g && window.g.translate ? window.g.translate(s) : s);

let uidCounter = 0;

export class FileSelekt {
	constructor(el, opts) {
		// The authored element IS the picker: anything but type="file" (the
		// classic type="hidden" mistake) renders a perfect-looking control
		// whose click opens NO dialog, silently. The intent is unambiguous —
		// coerce. The initial value rides the value ATTRIBUTE (readInitial),
		// which a type switch never touches.
		if (el.tagName === 'INPUT' && el.type !== 'file') el.type = 'file';
		this.el = el;
		this.opts = opts;
		this.uid = `fsel-${++uidCounter}`;
		this.mode = opts.mode;
		const imageMode = this.mode === 'image' || this.mode === 'image-editor';
		this.multiple = imageMode ? false : (opts.multiple !== null && opts.multiple !== undefined ? !!opts.multiple : (el.multiple || this.mode === 'dropzone' || this.mode === 'gallery'));
		this.readonly = !!opts.readonly;
		if (imageMode || this.mode === 'gallery') this.opts.accept = this.opts.accept || 'image';
		this.accept = parseAccept(this.opts.accept);
		this.withEditor = this.mode === 'image-editor' || this.mode === 'gallery';

		// entries: { id, data (jsonb), status: uploading|done|error, progress,
		// localUrl?, file? (kept for retry), error? }
		this.entries = [];

		this.build();
		this.readInitial();
		this.syncHolder(false);
		this.lastFired = this.signature();
		// a save mid-upload silently DROPPED the file (the race-conditions
		// scan): the holder carries only finished uploads, so former read
		// the record without it and the finished bytes landed unreferenced.
		// The submit waits for the upload instead.
		this.form = this.holder.closest('form');
		if (this.form) {
			this.onFormerSend = (e) => {
				if (this.entries.some((entry) => entry.status === 'uploading')) {
					e.preventDefault();
					window.g.warning(t('A file is still uploading — wait for it to finish.'));
				}
			};
			this.form.addEventListener('former:send', this.onFormerSend);
		}
		el.fileSelekt = this;
	}

	// ---- setup -----------------------------------------------------------------

	build() {
		const el = this.el;
		this.wrap = document.createElement('div');
		this.wrap.className = `fsel fsel-mode-${this.mode} fsel-shape-${this.opts.shape || 'rounded'}${this.readonly ? ' fsel-readonly' : ''}`;
		el.parentNode.insertBefore(this.wrap, el);
		this.wrap.appendChild(el);
		el.classList.add('fsel-picker');
		el.tabIndex = -1;
		el.setAttribute('aria-hidden', 'true');
		if (this.multiple) el.multiple = true;
		if (this.accept.attr) el.setAttribute('accept', this.accept.attr);

		// the picker never submits raw files - the hidden holder carries JSON
		this.holder = document.createElement('input');
		this.holder.type = 'hidden';
		this.holder.name = el.name || '';
		el.removeAttribute('name');
		this.wrap.appendChild(this.holder);
		this.holder.fileSelekt = this;

		this.body = document.createElement('div');
		this.body.className = 'fsel-body';
		this.wrap.appendChild(this.body);

		el.addEventListener('change', () => {
			this.addFiles(Array.from(el.files || []));
			el.value = '';
		});

		// paste (screenshots) - dropzone/gallery/image areas are focusable
		this.wrap.addEventListener('paste', (e) => {
			if (this.readonly) return;
			const files = Array.from(e.clipboardData ? e.clipboardData.files : []);
			if (files.length) { e.preventDefault(); this.addFiles(files); }
		});

		this.render();
	}

	readInitial() {
		const raw = String(this.el.getAttribute('value') || this.holder.value || '').trim();
		if (!raw) return;
		try { this.setEntriesFromJson(JSON.parse(raw)); } catch (err) { /* not JSON - ignore */ }
	}

	setEntriesFromJson(json) {
		const list = json ? (Array.isArray(json) ? json : [json]) : [];
		this.entries = list.filter(Boolean).map((data) => ({ id: `${this.uid}-${++uidCounter}`, data, status: 'done', progress: 1 }));
	}

	// ---- adding + uploading -------------------------------------------------------

	openPicker() {
		if (this.readonly) return;
		this.el.click();
	}

	validate(file) {
		const ext = extOf(file.name);
		if (this.accept.exts && !this.accept.exts.has(ext)) {
			window.g.warning(`${file.name}: ${t('file type not allowed')} (${this.opts.accept})`);
			return false;
		}
		if (this.opts.maxSize && file.size > this.opts.maxSize * 1024 * 1024) {
			window.g.warning(`${file.name}: ${t('file too large')} (max ${this.opts.maxSize} MB)`);
			return false;
		}
		return true;
	}

	async addFiles(files) {
		if (this.readonly || !files.length) return;
		let list = files.filter((f) => this.validate(f));
		if (!list.length) return;

		if (!this.multiple) {
			list = [list[0]];
			this.entries = []; // replace
		} else if (this.opts.maxFiles) {
			const room = this.opts.maxFiles - this.entries.length;
			if (list.length > room) {
				window.g.warning(`${t('Too many files')} (max ${this.opts.maxFiles})`);
				list = list.slice(0, Math.max(0, room));
			}
		}

		const added = list.map((file) => ({
			id: `${this.uid}-${++uidCounter}`,
			data: { name: file.name, ext: extOf(file.name), type: typeOf(extOf(file.name)), size: file.size },
			status: 'uploading',
			progress: 0,
			file,
			localUrl: typeOf(extOf(file.name)) === 'image' ? URL.createObjectURL(file) : null,
		}));
		this.entries = this.entries.concat(added);
		this.render();

		for (const entry of added) await this.upload(entry);

		// image-editor: open the crop right after the (single) file arrives
		if (this.mode === 'image-editor' && added[0] && added[0].status === 'done') this.editEntry(added[0]);
	}

	async upload(entry) {
		entry.status = 'uploading';
		entry.progress = 0;
		this.render();
		const file = await maybeDownscale(entry.file, this.opts.resize);
		if (!this.entries.includes(entry)) return; // removed/replaced mid-downscale — nothing to upload
		if (file !== entry.file) {
			// the DOWNSCALED file is what the server stores — the entry must
			// use it everywhere too (crop editor, previews), or crop
			// coordinates get computed in the original's pixel space and land
			// scaled/shifted on the stored image
			entry.file = file;
			if (entry.localUrl) {
				URL.revokeObjectURL(entry.localUrl);
				entry.localUrl = URL.createObjectURL(file);
			}
		}
		const res = await uploadFile(this.opts.endpoint, file, { field: this.holder.name, ...this.opts.params }, (p) => {
			entry.progress = p;
			const bar = this.body.querySelector(`[data-entry="${entry.id}"] .fsel-progress-bar`);
			if (bar) bar.style.width = `${Math.round(p * 100)}%`;
			const text = this.body.querySelector('.fsel-simple-progress');
			if (text) text.textContent = `${t('Uploading...')} ${Math.round(p * 100)}%`;
			const zoneBar = this.body.querySelector('.fsel-dropzone-progress .fsel-progress-bar');
			if (zoneBar) zoneBar.style.width = `${Math.round(p * 100)}%`;
			const zoneText = this.body.querySelector('.fsel-dropzone-uploading');
			if (zoneText) zoneText.textContent = this.uploadingText(p);
		});
		if (!this.entries.includes(entry)) { this.settled(); return; } // removed/replaced mid-upload (race scan): no ghost toast for an invisible file; the temp upload sweeps
		if (!res.success || (res.error && res.error.code)) {
			entry.status = 'error';
			entry.error = (res.error && res.error.message) || t('Upload failed');
			// toast for attention + the persistent inline state with Retry
			window.g.error(`${entry.data.name}: ${entry.error}`, { title: t('Upload failed') });
			this.emit('fsel:error', { file: entry.data, error: entry.error });
		} else {
			entry.status = 'done';
			entry.data = { ...res.data };
			this.emit('fsel:upload', { file: entry.data });
		}
		this.render();
		this.syncHolder(true);
		this.settled();
	}

	// One upload settled: when NOTHING is in flight any more the batch is
	// over — fsel:idle (BSP's ask, Sep 2026: "change fires per finished
	// file, nothing says the batch is over"), the moment clear() is safe.
	settled() {
		if (this.pending() === 0) this.emit('fsel:idle', { value: this.getValue() });
	}

	/** Uploads still in flight (count). */
	pending() {
		return this.entries.filter((e) => e.status === 'uploading').length;
	}

	/** True while any upload is in flight. */
	busy() {
		return this.pending() > 0;
	}

	// ---- entry actions ---------------------------------------------------------------

	entryById(id) {
		return this.entries.find((e) => e.id === id);
	}

	remove(id) {
		const entry = this.entryById(id);
		if (!entry) return;
		if (entry.localUrl) URL.revokeObjectURL(entry.localUrl);
		this.entries = this.entries.filter((e) => e !== entry);
		this.render();
		this.syncHolder(true);
	}

	reorder(from, to) {
		if (from === to || from < 0 || to < 0 || from >= this.entries.length || to >= this.entries.length) return;
		const [moved] = this.entries.splice(from, 1);
		this.entries.splice(to, 0, moved);
		this.render();
		this.syncHolder(true);
	}

	setCover(id) {
		this.entries.forEach((e) => { if (e.data) delete e.data.cover; });
		const entry = this.entryById(id);
		if (entry) entry.data.cover = true;
		this.render();
		this.syncHolder(true);
	}

	editEntry(entry) {
		if (this.readonly || entry.status !== 'done' || entry.data.type !== 'image') return;
		openEditor({
			url: this.originalUrl(entry),
			crop: entry.data.crop,
			// the editor frame ALWAYS matches the ratio the thumb is DISPLAYED
			// at (tiles/frames default to square) — a free-ratio frame would
			// mean cropping blind: object-fit cover trims the difference
			aspect: this.opts.shape === 'circle' ? '1:1' : (this.opts.aspect || '1:1'),
			round: this.opts.shape === 'circle',
			onApply: (crop) => {
				entry.data.crop = crop;
				entry.cropDirty = true; // local crop, newer than any server-rendered variant
				this.render();
				this.syncHolder(true);
			},
		});
	}

	// ---- value <-> holder ---------------------------------------------------------------

	doneData() {
		return this.entries.filter((e) => e.status === 'done').map((e) => e.data);
	}

	signature() {
		return JSON.stringify(this.doneData());
	}

	syncHolder(fireChange) {
		const done = this.doneData();
		this.holder.value = this.multiple ? JSON.stringify(done) : (done[0] ? JSON.stringify(done[0]) : '');
		const signature = this.signature();
		if (fireChange && signature !== this.lastFired) {
			this.lastFired = signature;
			this.holder.dispatchEvent(new Event('change', { bubbles: true }));
			this.emit('fsel:change', { value: this.getValue() });
		}
	}

	getValue() {
		const done = this.doneData();
		return this.multiple ? done : (done[0] || null);
	}

	// jsonb object/array (or JSON string) - e.g. the DB value on edit forms
	setValue(json) {
		if (typeof json === 'string' && json.trim()) json = JSON.parse(json);
		this.entries.forEach((e) => { if (e.localUrl) URL.revokeObjectURL(e.localUrl); });
		this.setEntriesFromJson(json);
		this.render();
		this.syncHolder(true);
	}

	// Drops every entry. While an upload is IN FLIGHT it REFUSES (false +
	// the same toast the former save shows) unless { force: true } — then
	// each running upload is dropped and announced as an fsel:error
	// ("Upload cancelled"), never silently (BSP's report, Sep 2026: a
	// clear() from a change handler ate a 20 MB upload with no signal).
	// Wait for fsel:idle (or pending() === 0) to clear safely.
	clear({ force = false } = {}) {
		if (!this.entries.length) return true;
		const running = this.entries.filter((e) => e.status === 'uploading');
		if (running.length && !force) {
			window.g.warning(t('A file is still uploading — wait for it to finish.'));
			return false;
		}
		this.entries.forEach((e) => { if (e.localUrl) URL.revokeObjectURL(e.localUrl); });
		this.entries = [];
		for (const entry of running) {
			entry.status = 'error';
			entry.error = t('Upload cancelled');
			this.emit('fsel:error', { file: entry.data, error: entry.error, cancelled: true });
		}
		this.render();
		this.syncHolder(true);
		if (running.length) this.settled();
		return true;
	}

	emit(name, detail) {
		this.holder.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { fileSelekt: this, ...detail } }));
	}

	// ---- rendering -------------------------------------------------------------------------

	previewUrl(entry) {
		return entry.localUrl || displayUrl(entry.data, this.opts.thumbKey);
	}

	// the UN-CROPPED source - crop coordinates only make sense against it
	originalUrl(entry) {
		if (entry.localUrl) return entry.localUrl;
		if (!entry.data.path) return '';
		if (/^https?:\/\//.test(entry.data.path)) return entry.data.path; // public CDN paths arrive absolute
		const base = String((window.g.settings || {}).filesUrl || '').replace(/\/+$/, '');
		return `${base}/${entry.data.path}`;
	}

	// Thumbnail element. Crop handling: a crop the SERVER already rendered
	// (variants.cropped, from a saved record) is shown as a plain image; a
	// LOCAL crop (fresh upload / re-crop, entry.cropDirty) is drawn on a
	// canvas from the ORIGINAL image - never from a variant: variants
	// already contain the rotation+extract, re-applying the coordinates on
	// them shows a shifted/derotated frame.
	thumbHtmlInto(container, entry) {
		const serverRendered = entry.data.variants && entry.data.variants.cropped && !entry.cropDirty;
		if (entry.data.crop && !serverRendered) {
			const url = this.originalUrl(entry);
			if (!url) return;
			const canvas = document.createElement('canvas');
			canvas.className = 'fsel-thumb-media';
			const img = new Image();
			img.onload = () => drawCropPreview(canvas, img, entry.data.crop);
			img.src = url;
			container.appendChild(canvas);
			return;
		}
		const url = this.previewUrl(entry);
		if (!url) return;
		const img = document.createElement('img');
		img.className = 'fsel-thumb-media';
		img.alt = entry.data.name || '';
		img.src = url;
		container.appendChild(img);
	}

	render() {
		this.body.innerHTML = '';
		if (this.mode === 'simple') this.renderSimple();
		else if (this.mode === 'dropzone') this.renderDropzone();
		else if (this.mode === 'gallery') this.renderGallery();
		else this.renderImage();
		this.renderFieldError();
	}

	// House rule: error MESSAGES live under the element (after any list/grid),
	// through the shared g.fieldError slot - the same one the validation
	// library uses. Inline we only keep the red visuals + Retry.
	renderFieldError() {
		const failed = this.entries.filter((e) => e.status === 'error');
		const message = failed.map((e) => `${e.data.name}: ${e.error || t('Upload failed')}`).join('\n');
		window.g.fieldError(this.holder, message || null);
	}

	// -- simple: inline input-group control (display + icon button) --
	renderSimple() {
		const group = document.createElement('div');
		group.className = 'input-group fsel-simple';

		const display = document.createElement('div');
		display.className = 'form-control fsel-simple-display';
		const uploading = this.entries.find((e) => e.status === 'uploading');
		const errored = this.entries.find((e) => e.status === 'error');
		const done = this.entries.filter((e) => e.status === 'done');

		if (uploading) {
			display.innerHTML = `<i class="ri-loader-4-line fsel-spin" aria-hidden="true"></i><span class="fsel-simple-text fsel-simple-progress">${t('Uploading...')} ${Math.round(uploading.progress * 100)}%</span>`;
		} else if (errored) {
			display.innerHTML = `<i class="ri-error-warning-line fsel-simple-error-icon" aria-hidden="true"></i><span class="fsel-simple-text fsel-simple-error">${escapeHtml(errored.data.name || t('Upload failed'))}</span>`;
			const retry = rowButton('ri-restart-line', t('Retry'));
			retry.addEventListener('click', (e) => { e.stopPropagation(); this.upload(errored); });
			display.appendChild(retry);
		} else if (!done.length) {
			display.innerHTML = `<span class="fsel-simple-text is-placeholder">${escapeHtml(this.opts.placeholder || (this.multiple ? t('Choose files...') : t('Choose file...')))}</span>`;
		} else if (done.length === 1) {
			display.innerHTML = `<i class="${iconOf(done[0].data.ext)}" aria-hidden="true"></i><span class="fsel-simple-text">${escapeHtml(done[0].data.name || '')}</span>`;
		} else {
			display.innerHTML = `<i class="ri-file-copy-2-line" aria-hidden="true"></i><span class="fsel-simple-text">${done.length} ${t('files loaded')}</span>`;
		}

		if (!this.readonly && this.opts.clearable && (done.length || errored) && !uploading) {
			const clear = rowButton('ri-close-line', t('Clear'));
			clear.addEventListener('click', (e) => { e.stopPropagation(); this.clear(); });
			display.appendChild(clear);
		}
		if (!this.readonly) display.addEventListener('click', () => this.openPicker());

		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'btn btn-secondary fsel-simple-btn';
		btn.disabled = this.readonly;
		btn.setAttribute('aria-label', this.opts.placeholder || (this.multiple ? t('Choose files...') : t('Choose file...')));
		btn.innerHTML = '<i class="ri-upload-2-line" aria-hidden="true"></i>';
		btn.addEventListener('click', () => this.openPicker());

		group.append(display, btn);
		this.body.appendChild(group);
	}

	// -- dropzone: drop area + rows --
	renderDropzone() {
		const zone = document.createElement('div');
		zone.className = 'fsel-dropzone';
		zone.tabIndex = this.readonly ? -1 : 0;
		zone.setAttribute('role', 'button');
		zone.innerHTML = `<i class="ri-upload-cloud-2-line fsel-dropzone-icon" aria-hidden="true"></i><div class="fsel-dropzone-text">${this.opts.placeholder || t('Drag & drop files here, click to browse, or paste')}</div>`;
		this.bindDropTarget(zone);
		this.body.appendChild(zone);
		// rows: false — the drop area and nothing under it (the page owns
		// the list); the zone itself shows the flight: a count + a bar
		if (this.opts.rows === false) {
			const running = this.entries.filter((e) => e.status === 'uploading');
			if (running.length) {
				zone.classList.add('is-uploading');
				const first = running[0];
				zone.insertAdjacentHTML('beforeend', `<div class="fsel-dropzone-uploading">${this.uploadingText(first.progress)}</div><span class="fsel-progress fsel-dropzone-progress"><span class="fsel-progress-bar" style="width:${Math.round(first.progress * 100)}%"></span></span>`);
			}
			return;
		}
		if (this.entries.length) this.body.appendChild(this.renderRows());
	}

	uploadingText(progress) {
		const n = this.pending();
		const pct = `${Math.round((progress || 0) * 100)}%`;
		return n > 1 ? `${t('Uploading {count} files...', { count: n })} ${pct}` : `${t('Uploading...')} ${pct}`;
	}

	bindDropTarget(zone) {
		zone.addEventListener('click', () => this.openPicker());
		zone.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openPicker(); } });
		zone.addEventListener('dragover', (e) => { e.preventDefault(); if (!this.readonly) zone.classList.add('is-over'); });
		zone.addEventListener('dragleave', () => zone.classList.remove('is-over'));
		zone.addEventListener('drop', (e) => {
			e.preventDefault();
			zone.classList.remove('is-over');
			if (!this.readonly) this.addFiles(Array.from(e.dataTransfer.files || []));
		});
	}

	renderRows() {
		const list = document.createElement('div');
		list.className = 'fsel-rows';
		this.entries.forEach((entry, index) => {
			const row = document.createElement('div');
			row.className = `fsel-row is-${entry.status}`;
			row.setAttribute('data-entry', entry.id);
			row.innerHTML = `<i class="${iconOf(entry.data.ext)} fsel-row-icon" aria-hidden="true"></i><span class="fsel-row-name">${escapeHtml(entry.data.name || '')}</span><span class="fsel-row-size">${entry.data.size ? humanSize(entry.data.size) : ''}</span>`;
			if (entry.status === 'uploading') {
				row.insertAdjacentHTML('beforeend', `<span class="fsel-progress"><span class="fsel-progress-bar" style="width:${Math.round(entry.progress * 100)}%"></span></span>`);
			}
			if (entry.status === 'error') {
				const retry = rowButton('ri-restart-line', t('Retry'));
				retry.addEventListener('click', () => this.upload(entry));
				row.appendChild(retry);
			}
			if (!this.readonly) {
				const del = rowButton('ri-close-line', t('Remove'));
				del.addEventListener('click', () => this.remove(entry.id));
				row.appendChild(del);
			}
			if (this.multiple && !this.readonly && this.entries.length > 1) this.bindDrag(row, index, list);
			list.appendChild(row);
		});
		return list;
	}

	// -- image / image-editor: framed thumb --
	renderImage() {
		const entry = this.entries[0];
		const frame = document.createElement('div');
		frame.className = 'fsel-frame';
		frame.style.aspectRatio = frameRatio(this.opts.aspect);

		if (!entry) {
			frame.classList.add('is-empty');
			frame.tabIndex = this.readonly ? -1 : 0;
			frame.setAttribute('role', 'button');
			frame.innerHTML = `<i class="ri-image-add-line" aria-hidden="true"></i><span>${this.opts.placeholder || t('Upload image')}</span>`;
			this.bindDropTarget(frame);
			this.body.appendChild(frame);
			return;
		}

		this.thumbHtmlInto(frame, entry);
		if (entry.status === 'uploading') {
			frame.insertAdjacentHTML('beforeend', `<span class="fsel-progress fsel-progress-overlay"><span class="fsel-progress-bar" style="width:${Math.round(entry.progress * 100)}%"></span></span>`);
			frame.setAttribute('data-entry', entry.id);
		}
		if (entry.status === 'error') frame.classList.add('is-error');
		if (!this.readonly) {
			const actions = document.createElement('div');
			actions.className = 'fsel-actions';
			if (this.withEditor && entry.status === 'done') actions.appendChild(this.actionButton('ri-crop-line', t('Crop'), () => this.editEntry(entry)));
			actions.appendChild(this.actionButton('ri-image-edit-line', t('Change'), () => this.openPicker()));
			if (entry.status === 'error') actions.appendChild(this.actionButton('ri-restart-line', t('Retry'), () => this.upload(entry)));
			if (this.opts.clearable) actions.appendChild(this.actionButton('ri-delete-bin-line', t('Delete'), () => this.remove(entry.id)));
			frame.appendChild(actions);
		}
		this.body.appendChild(frame);
	}

	// -- gallery: tile grid --
	renderGallery() {
		const grid = document.createElement('div');
		grid.className = 'fsel-grid';
		const coverIndex = this.opts.cover ? Math.max(0, this.entries.findIndex((e) => e.data.cover)) : -1;

		this.entries.forEach((entry, index) => {
			const tile = document.createElement('div');
			tile.className = `fsel-tile is-${entry.status}`;
			tile.style.aspectRatio = frameRatio(this.opts.aspect); // display ratio = the crop editor's frame ratio
			tile.setAttribute('data-entry', entry.id);
			this.thumbHtmlInto(tile, entry);
			if (entry.status === 'uploading') tile.insertAdjacentHTML('beforeend', `<span class="fsel-progress fsel-progress-overlay"><span class="fsel-progress-bar" style="width:${Math.round(entry.progress * 100)}%"></span></span>`);

			if (this.opts.cover && index === coverIndex) tile.insertAdjacentHTML('beforeend', `<span class="fsel-cover-badge">${t('Cover')}</span>`);

			if (!this.readonly) {
				const actions = document.createElement('div');
				actions.className = 'fsel-actions';
				if (this.withEditor && entry.status === 'done') actions.appendChild(this.actionButton('ri-crop-line', t('Crop'), () => this.editEntry(entry)));
				if (this.opts.cover && index !== coverIndex) actions.appendChild(this.actionButton('ri-star-line', t('Set cover'), () => this.setCover(entry.id)));
				if (entry.status === 'error') actions.appendChild(this.actionButton('ri-restart-line', t('Retry'), () => this.upload(entry)));
				actions.appendChild(this.actionButton('ri-delete-bin-line', t('Delete'), () => this.remove(entry.id)));
				tile.appendChild(actions);
				this.bindDrag(tile, index, grid);
			}

			const cell = document.createElement('div');
			cell.className = 'fsel-cell';
			cell.appendChild(tile);
			if (this.opts.captions) {
				const caption = document.createElement('input');
				caption.type = 'text';
				caption.className = 'form-control form-control-sm fsel-caption';
				caption.placeholder = t('Caption...');
				caption.value = entry.data.caption || '';
				caption.readOnly = this.readonly;
				caption.addEventListener('change', () => {
					if (caption.value.trim()) entry.data.caption = caption.value.trim();
					else delete entry.data.caption;
					this.syncHolder(true);
				});
				cell.appendChild(caption);
			}
			grid.appendChild(cell);
		});

		if (!this.readonly) {
			const add = document.createElement('div');
			add.className = 'fsel-tile fsel-tile-add';
			add.tabIndex = 0;
			add.setAttribute('role', 'button');
			add.style.aspectRatio = frameRatio(this.opts.aspect);
			add.innerHTML = `<i class="ri-add-line" aria-hidden="true"></i>`;
			this.bindDropTarget(add);
			const cell = document.createElement('div');
			cell.className = 'fsel-cell';
			cell.appendChild(add);
			grid.appendChild(cell);
		}
		this.body.appendChild(grid);
	}

	actionButton(icon, label, onClick) {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'fsel-action';
		btn.title = label;
		btn.setAttribute('aria-label', label);
		btn.innerHTML = `<i class="${icon}" aria-hidden="true"></i>`;
		btn.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
		return btn;
	}

	// drag & drop reorder (rows + tiles share it)
	bindDrag(el, index, container) {
		el.draggable = true;
		el.addEventListener('dragstart', (e) => {
			this.dragIndex = index;
			e.dataTransfer.effectAllowed = 'move';
			el.classList.add('is-dragging');
		});
		el.addEventListener('dragend', () => el.classList.remove('is-dragging'));
		el.addEventListener('dragover', (e) => {
			if (this.dragIndex === undefined || this.dragIndex === null) return;
			e.preventDefault();
			e.stopPropagation();
		});
		el.addEventListener('drop', (e) => {
			if (this.dragIndex === undefined || this.dragIndex === null) return;
			e.preventDefault();
			e.stopPropagation();
			const from = this.dragIndex;
			this.dragIndex = null;
			this.reorder(from, index);
		});
	}

	destroy() {
		if (this.form && this.onFormerSend) this.form.removeEventListener('former:send', this.onFormerSend);
		this.entries.forEach((e) => { if (e.localUrl) URL.revokeObjectURL(e.localUrl); });
		if (this.holder.name) this.el.name = this.holder.name;
		this.wrap.parentNode.insertBefore(this.el, this.wrap);
		this.wrap.remove();
		this.el.classList.remove('fsel-picker');
		this.el.removeAttribute('aria-hidden');
		this.el.tabIndex = 0;
		delete this.el.fileSelekt;
	}
}

function rowButton(icon, label) {
	const btn = document.createElement('button');
	btn.type = 'button';
	btn.className = 'fsel-row-btn';
	btn.title = label;
	btn.setAttribute('aria-label', label);
	btn.innerHTML = `<i class="${icon}" aria-hidden="true"></i>`;
	return btn;
}

function frameRatio(aspect) {
	if (typeof aspect === 'number' && aspect > 0) return String(aspect);
	if (typeof aspect === 'string' && aspect.includes(':')) {
		const [w, h] = aspect.split(':').map(Number);
		if (w > 0 && h > 0) return `${w} / ${h}`;
	}
	return '1';
}

function escapeHtml(s) {
	return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
